import { Box, Typography, alpha, useTheme } from "@mui/material"
import { ContainmentErrorMessages } from "lib/api"
import { motion } from "framer-motion"

// Define motion components
/* eslint-disable-next-line*/
const MotionBox = motion(Box)
/* eslint-disable-next-line*/
const MotionTypography = motion(Typography)

// Animation variants
const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: {
			when: "beforeChildren",
			staggerChildren: 0.1,
			duration: 0.3,
		},
	},
}

const itemVariants = {
	hidden: { x: -10, opacity: 0 },
	visible: {
		x: 0,
		opacity: 1,
		transition: {
			type: "spring" as const,
			stiffness: 100,
			damping: 15,
		},
	},
}

type errorSummaryProps = {
	errorList: ContainmentErrorMessages[]
}

export function ErrorSummary({ errorList }: errorSummaryProps) {
	const theme = useTheme()

	if (!errorList || errorList.length === 0) {
		return (
			<MotionBox
				initial="hidden"
				animate="visible"
				variants={containerVariants}
				sx={{
					textAlign: "center",
					p: 2,
				}}
			>
				<MotionTypography
					variant="body1"
					variants={itemVariants}
					sx={{
						fontWeight: 500,
						color: theme.palette.error.main,
					}}
				>
					An unexpected error occurred. Please try again later.
				</MotionTypography>
			</MotionBox>
		)
	}

	return (
		<MotionBox
			initial="hidden"
			animate="visible"
			variants={containerVariants}
			sx={{
				borderRadius: 3,
			}}
		>
			<MotionTypography
				variant="h6"
				variants={itemVariants}
				sx={{
					mb: 2,
					fontWeight: 600,
					background: `linear-gradient(45deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
					backgroundClip: "text",
					textFillColor: "transparent",
					WebkitBackgroundClip: "text",
					WebkitTextFillColor: "transparent",
				}}
			>
				Issues Detected
			</MotionTypography>

			{errorList.Map((error, index) => (
				<MotionBox
					key={index}
					variants={itemVariants}
					sx={{
						mb: 1.5,
						p: 2,
						borderRadius: 2,
						backgroundColor: alpha(theme.palette.error.light, 0.08),
						borderLeft: `4px solid ${theme.palette.error.main}`,
						transition: "transform 0.2s ease, box-shadow 0.2s ease",
						overflow: "wrap",
						wordBreak: "break-word",
					}}
					width={"100%"}
				>
					<MotionTypography
						variant="body2"
						sx={{
							fontWeight: error.field ? 600 : 400,
							color: error.field
								? theme.palette.error.dark
								: theme.palette.text.primary,
							display: "flex",
							flexWrap: "wrap",
						}}
					>
						{error.field && (
							<Box
								sx={{
									fontWeight: 700,
									color: theme.palette.error.main,
									mr: 1,
								}}
							>
								{error.field}:
							</Box>
						)}
						{error.message}
						{error.fieldValue && (
							<Box
								sx={{
									ml: 1,
									fontStyle: "italic",
									color: alpha(theme.palette.text.primary, 0.7),
								}}
							>
								{error.fieldValue}
							</Box>
						)}
					</MotionTypography>
				</MotionBox>
			))}
		</MotionBox>
	)
}
